import { z } from 'zod';
/**
 * The root helper's contract (knowledge doc section 18).
 *
 * The helper is the only thing on a monitored server that runs as root, and rule 3
 * is what it exists to enforce: **nothing reaches a shell as free text**. It accepts
 * a named operation from a fixed list, validates that operation's arguments against
 * its own schema, and runs a program with an argument array. There is no path
 * through it that takes a string and executes it.
 *
 * The wire shapes live here rather than in the helper package because both ends
 * need them — the agent writes the request and the helper reads it — and a socket
 * between two processes is a boundary like any other.
 */
/** Section 18: owned by root and group `bartizo`, mode 660. */
export const HELPER_SOCKET_PATH = '/run/bartizo/helper.sock';
export const HELPER_SOCKET_MODE = 0o660;
export const HELPER_SOCKET_DIR_MODE = 0o750;
/**
 * The largest request the helper will read.
 *
 * Rule 14 applied to a socket: without a ceiling, anything that can connect can
 * make the root process allocate until the machine stops. A megabyte is far above
 * the largest legitimate call — `set_fim_paths` with two hundred maximum-length
 * paths is about four hundred kilobytes — and far below anything dangerous.
 */
export const HELPER_MAX_FRAME_BYTES = 1024 * 1024;
/** A connection that has not sent a complete request by then is dropped. */
export const HELPER_IDLE_TIMEOUT_MS = 10_000;
/** What an operation gets if it does not name its own ceiling. */
export const HELPER_DEFAULT_TIMEOUT_MS = 30_000;
/**
 * How long a caller waits for an answer.
 *
 * Comfortably above the per-operation default, because the caller cannot know
 * which operation it asked for or what ceiling that one declared — a web server
 * reload and a `du` scan are not the same wait. The helper stops its own work on
 * time; this exists so a caller is not left holding a socket that will never
 * answer, which is a different failure with a different cause.
 */
export const HELPER_CLIENT_TIMEOUT_MS = 120_000;
/**
 * The operations that wait on the internet — the CrowdSec Hub — and may take longer
 * than `HELPER_CLIENT_TIMEOUT_MS`: a caller waits a little past each one's own
 * ceiling, so it hears the helper's answer rather than giving up first (P11.9.2).
 * Kept beside the operations' ceilings in the helper, which a test holds equal.
 */
export const HELPER_SLOW_OPERATIONS = {
    install_crowdsec_collection: 250_000,
    upgrade_crowdsec_hub: 370_000,
    set_web_bouncer: 430_000,
    /**
     * P11.8.2: a running Wazuh agent takes about 30 s to stop (wazuh-modulesd is
     * killed after its grace period), and its unit allows 45 s each to stop and
     * start; a refused start is followed by a restart onto the old files.
     */
    enrol_wazuh_agent: 250_000,
    /** P11.19: each fix has its own ceiling and `apt-get` is the slow part; then the Wazuh restart. */
    apply_compliance_fixes: 610_000,
};
export const HelperRequest = z.object({
    /**
     * Correlates the agent's log line with the helper's. Not a credential: the
     * kernel decides who may connect, and nothing here is trusted to say who it is.
     */
    id: z.uuid(),
    /**
     * A bounded string rather than `OperationName`, deliberately.
     *
     * The registry is the authority on which operations exist — that is the whole of
     * rule 3 — and putting the enum here too would give the question two answers.
     * Worse, the wrong one: a name outside the enum would fail the *request* schema,
     * so the helper could not tell the caller it asked for something that is not an
     * operation, only that its request was malformed. Callers build these from
     * `OperationName` and get the enum from the type; the helper reads a string and
     * asks the registry.
     */
    operation: z.string().min(1).max(64),
    /**
     * Deliberately `unknown`. The registry validates it against the schema for that
     * specific operation, so a wrong shape cannot reach a handler — checking it here
     * would mean a second, looser description of the same thing.
     */
    args: z.unknown(),
});
/**
 * Why a call did not succeed.
 *
 * Separated because section 12's `CommandResult` already distinguishes them: a
 * `rejected` command is one the helper would never run, and a `failed` one ran and
 * did not work. Collapsing the two would make an operator unable to tell a bug in
 * the console from a broken server.
 */
export const HelperFailure = z.enum([
    /** Not in the registry. The list is fixed at build time (rule 3). */
    'unknown_operation',
    /** The operation exists; these arguments are not its arguments. */
    'invalid_args',
    /** Valid arguments the operation will not act on: a path outside its prefixes. */
    'refused',
    /** It ran, and it did not work. */
    'failed',
    /** It ran too long and was stopped. */
    'timeout',
    /** The helper could not be reached at all. Set by the client, never on the wire. */
    'unavailable',
]);
export const HelperOutcome = z.object({
    ok: z.boolean(),
    reason: HelperFailure.optional(),
    /**
     * For a person reading a command result. Bounded, and rule 5 applies: an
     * operation puts what went wrong here, never a secret it was working with.
     */
    detail: z.string().max(1000).optional(),
    /**
     * What a read-only operation found (`inspect_web_config`, P5.5). `unknown` here
     * because each operation has its own result schema, and the caller parses it
     * with that one: the helper is root, but its answer still crosses a boundary.
     * The frame ceiling (`HELPER_MAX_FRAME_BYTES`) bounds it.
     */
    data: z.unknown().optional(),
});
export const HelperResponse = HelperOutcome.extend({ id: z.uuid() });
//# sourceMappingURL=helper.js.map